import { c } from 'ttag';

import { IDEAL_WERO_BRAND_NAME } from '@proton/chargebee/lib/constants';
import type { IconComponent } from '@proton/icons/component';
import { IcBank } from '@proton/icons/icons/IcBank';
import { IcBrandAmex } from '@proton/icons/icons/IcBrandAmex';
import { IcBrandApple } from '@proton/icons/icons/IcBrandApple';
import { IcBrandBitcoin } from '@proton/icons/icons/IcBrandBitcoin';
import { IcBrandDiscover } from '@proton/icons/icons/IcBrandDiscover';
import { IcBrandGoogle } from '@proton/icons/icons/IcBrandGoogle';
import { IcBrandMastercard } from '@proton/icons/icons/IcBrandMastercard';
import { IcBrandPaypal } from '@proton/icons/icons/IcBrandPaypal';
import { IcBrandVisa } from '@proton/icons/icons/IcBrandVisa';
import { IcCreditCard } from '@proton/icons/icons/IcCreditCard';
import { IcMoneyBills } from '@proton/icons/icons/IcMoneyBills';
import { PAYMENT_METHOD_TYPES } from '@proton/payments/core/constants';
import { isSignupFlow } from '@proton/payments/core/helpers';
import type {
    AvailablePaymentMethod,
    PaymentMethodFlow,
    SavedMethodDetails,
    SavedMethodType,
    SavedPaymentMethod,
    SepaDetails,
} from '@proton/payments/core/interface';
import { isAndroid, isIos } from '@proton/shared/lib/helpers/browser';
import isTruthy from '@proton/utils/isTruthy';

import type { MethodsHook } from '../react-extensions/useMethods';

export interface ViewPaymentMethod extends AvailablePaymentMethod {
    readonly icon?: IconComponent | undefined;
    readonly text: string;
}

export interface ClientMethodsHook extends MethodsHook {
    usedMethods: ViewPaymentMethod[];
    newMethods: ViewPaymentMethod[];
    allMethods: ViewPaymentMethod[];
    lastUsedMethod: ViewPaymentMethod | undefined;
}

const cardIconByLowercaseBrand: Partial<Record<string, IconComponent>> = {
    'american express': IcBrandAmex,
    visa: IcBrandVisa,
    mastercard: IcBrandMastercard,
    discover: IcBrandDiscover,
};

// CHARGEBEE_IDEAL icon is overriden in the paymentMetodSelector
const savedMethodIconByType: Partial<Record<SavedMethodType, IconComponent>> = {
    [PAYMENT_METHOD_TYPES.CHARGEBEE_PAYPAL]: IcBrandPaypal,
    [PAYMENT_METHOD_TYPES.CHARGEBEE_SEPA_DIRECT_DEBIT]: IcBank,
    [PAYMENT_METHOD_TYPES.APPLE_PAY]: IcBrandApple,
    [PAYMENT_METHOD_TYPES.GOOGLE_PAY]: IcBrandGoogle,
};

const getIcon = (paymentMethod: SavedPaymentMethod): IconComponent | undefined => {
    if (paymentMethod.Type === PAYMENT_METHOD_TYPES.CHARGEBEE_CARD) {
        return cardIconByLowercaseBrand[paymentMethod.Details.Brand.toLowerCase()] ?? IcCreditCard;
    }

    return savedMethodIconByType[paymentMethod.Type];
};

const NBSP_HTML = '\u00A0';

export function formattedShortSavedSepaDetails(details: SepaDetails): string {
    const { Country, Last4 } = details;

    return `${Country}${NBSP_HTML}••••${NBSP_HTML}${Last4}`;
}

export function formattedSavedSepaDetails(details: SepaDetails): string {
    const iban = formattedShortSavedSepaDetails(details);
    return `IBAN${NBSP_HTML}${iban}`;
}

const getBankTransferLabel = (details: SepaDetails): string => {
    const iban = formattedSavedSepaDetails(details);

    // translator: for example "Bank transfer - IBAN •••• 0000"
    return c('Payments.Saved payment method details').t`Bank transfer - ${iban}`;
};

const savedMethodLabelByType: {
    [T in SavedMethodType]: (details: Extract<SavedPaymentMethod, { Type: T }>['Details']) => string;
} = {
    [PAYMENT_METHOD_TYPES.CHARGEBEE_CARD]: ({ Brand, Last4 }) =>
        // translator: example would be: "Mastercard" ending in "7777"
        c('new_plans: info').t`${Brand} ending in ${Last4}`,
    [PAYMENT_METHOD_TYPES.CHARGEBEE_PAYPAL]: ({ PayerID }) => `PayPal - ${PayerID}`,
    [PAYMENT_METHOD_TYPES.CHARGEBEE_IDEAL]: getBankTransferLabel,
    [PAYMENT_METHOD_TYPES.CHARGEBEE_SEPA_DIRECT_DEBIT]: getBankTransferLabel,
    [PAYMENT_METHOD_TYPES.APPLE_PAY]: ({ Last4 }) =>
        // translator: example "Apple Pay - card ending in 1234". Please do not translate brand "Apple Pay".
        c('Payments.Saved payment method details').t`Apple Pay - card ending in ${Last4}`,
    [PAYMENT_METHOD_TYPES.GOOGLE_PAY]: ({ Last4 }) =>
        // translator: example "Google Pay - card ending in 1234". Please do not translate brand "Google Pay".
        c('Payments.Saved payment method details').t`Google Pay - card ending in ${Last4}`,
};

const getMethod = ({ Type, Details }: SavedPaymentMethod): string => {
    const getLabel = savedMethodLabelByType[Type] as ((details: SavedMethodDetails) => string) | undefined;

    return getLabel?.(Details) ?? '';
};

type NewMethodView = { icon?: IconComponent; text: string };

const newMethodViewByType: Partial<Record<PAYMENT_METHOD_TYPES, () => NewMethodView>> = {
    [PAYMENT_METHOD_TYPES.CHARGEBEE_BITCOIN]: () => ({
        icon: IcBrandBitcoin,
        text: c('Payment method option').t`Bitcoin`,
    }),
    [PAYMENT_METHOD_TYPES.CASH]: () => ({ icon: IcMoneyBills, text: c('Label').t`Cash` }),
    [PAYMENT_METHOD_TYPES.CHARGEBEE_CARD]: () => ({
        icon: IcCreditCard,
        text: c('Payment method option').t`Credit/debit card`,
    }),
    [PAYMENT_METHOD_TYPES.CHARGEBEE_PAYPAL]: () => ({
        icon: IcBrandPaypal,
        text: c('Payment method option').t`PayPal`,
    }),
    [PAYMENT_METHOD_TYPES.CHARGEBEE_SEPA_DIRECT_DEBIT]: () => ({
        icon: IcBank,
        text: c('Payment method option').t`Bank transfer`,
    }),
    [PAYMENT_METHOD_TYPES.APPLE_PAY]: () => ({ icon: IcBrandApple, text: c('Payment method option').t`Apple Pay` }),
    [PAYMENT_METHOD_TYPES.GOOGLE_PAY]: () => ({ text: c('Payment method option').t`Google Pay` }),
    [PAYMENT_METHOD_TYPES.CHARGEBEE_IDEAL]: () => ({ text: IDEAL_WERO_BRAND_NAME }),
};

/**
 * Transform the payment method object from the react-extensions package to a view model that can be used in the UI.
 */
function convertMethod(
    method: AvailablePaymentMethod,
    getSavedMethodById: MethodsHook['getSavedMethodByID'],
    flow: PaymentMethodFlow
): ViewPaymentMethod {
    if (method.paymentMethodId) {
        const savedMethod = getSavedMethodById(method.paymentMethodId) as SavedPaymentMethod;

        return {
            icon: getIcon(savedMethod),
            text: [getMethod(savedMethod), method.isExpired && `(${c('Info').t`Expired`})`].filter(Boolean).join(' '),
            ...method,
        };
    }

    const newMethodView = newMethodViewByType[method.type]?.();
    if (newMethodView) {
        return { ...newMethodView, ...method };
    }

    return {
        icon: IcCreditCard,
        text: isSignupFlow(flow)
            ? c('Payment method option').t`Credit/debit card`
            : c('Payment method option').t`New credit/debit card`,
        ...method,
    };
}

/**
 * Enhance the methods hook with client specific data like icons and text.
 * @param methodsHook - output of the useMethods hook from the react-extensions package
 * @param flow – current payment flow. Might modify the text of the payment methods
 * @returns
 */
export const wrapMethods = (methodsHook: MethodsHook, flow: PaymentMethodFlow): ClientMethodsHook => {
    const { getSavedMethodByID, usedMethods, newMethods, allMethods, lastUsedMethod } = methodsHook;

    return {
        ...methodsHook,
        usedMethods: usedMethods.map((method) => convertMethod(method, getSavedMethodByID, flow)),
        newMethods: newMethods.map((method) => convertMethod(method, getSavedMethodByID, flow)),
        allMethods: allMethods.map((method) => convertMethod(method, getSavedMethodByID, flow)),
        lastUsedMethod: lastUsedMethod && convertMethod(lastUsedMethod, getSavedMethodByID, flow),
    };
};

export const sortMethodsBasedOnDevice = (availablePaymentMethods: AvailablePaymentMethod[]) => {
    const applePay = availablePaymentMethods.find(({ type }) => type === PAYMENT_METHOD_TYPES.APPLE_PAY);
    const googlePay = availablePaymentMethods.find(({ type }) => type === PAYMENT_METHOD_TYPES.GOOGLE_PAY);
    const everythingElse = availablePaymentMethods.filter(
        ({ type }) => type !== PAYMENT_METHOD_TYPES.APPLE_PAY && type !== PAYMENT_METHOD_TYPES.GOOGLE_PAY
    );

    let sortedPaymentMethods: (AvailablePaymentMethod | undefined)[] = availablePaymentMethods;
    if (isIos()) {
        sortedPaymentMethods = [applePay, ...everythingElse, googlePay];
    } else if (isAndroid()) {
        sortedPaymentMethods = [googlePay, ...everythingElse, applePay];
    }
    return sortedPaymentMethods.filter(isTruthy);
};
